
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Obtiene el estado actual del carrito sin modificarlo.
      async fetchCartSnapshot() {
        const response = await fetch(
          `${routes.cart_url}.js`,
          { cache: 'no-store' }
        );

        if (!response.ok) {
          throw new Error('No se pudo consultar el carrito');
        }

        return response.json();
      }

      // Identifica las unidades añadidas mediante la diferencia
      // entre el carrito anterior y el carrito actualizado.
      getAddedQuantity(beforeCart, afterCart, variantId) {
        const getQuantity = (cart) => {
          return (cart.items || [])
            .filter(
              (item) =>
                String(item.variant_id) === String(variantId)
            )
            .reduce(
              (total, item) => total + item.quantity,
              0
            );
        };

        return (
          getQuantity(afterCart) -
          getQuantity(beforeCart)
        );
      }

      // Determina qué línea corresponde al producto añadido.
      getAddedItem(beforeCart, afterCart, variantId) {
        const previousItems = new Map(
          (beforeCart.items || []).map(
            (item) => [item.key, item.quantity]
          )
        );

        return (afterCart.items || []).find((item) => {
          return (
            String(item.variant_id) === String(variantId) &&
            item.quantity > (previousItems.get(item.key) || 0)
          );
        });
      }

      // Gestiona los casos en los que Shopify confirma
      // una adición parcial mediante el cambio real del carrito.
      async handlePartialAddition(
        response,
        variantId,
        requestedQuantity,
        beforeCart,
        deferred
      ) {
        if (
          Number(response.status) !== 422 ||
          !beforeCart
        ) {
          return false;
        }

        let afterCart;

        try {
          afterCart = await this.fetchCartSnapshot();
        } catch (error) {
          console.warn(
            'No se pudo verificar la adición parcial:',
            error
          );

          return false;
        }

        const addedQuantity = this.getAddedQuantity(
          beforeCart,
          afterCart,
          variantId
        );

        // Si no se añadieron unidades, dejamos que
        // Shopify muestre el error de disponibilidad.
        if (addedQuantity <= 0) {
          return false;
        }

        const addedItem = this.getAddedItem(
          beforeCart,
          afterCart,
          variantId
        );

        this.error = false;
        this.handleErrorMessage();

        this.resolveCartLinesUpdate(deferred);

        // Alternativa segura para otros tipos de carrito.
        if (
          !this.cart ||
          !addedItem ||
          typeof this.cart.renderPartialContents !== 'function'
        ) {
          window.location.href = routes.cart_url;
          return true;
        }

        const renderNotification = async () => {
          const cartData =
            await this.cart.renderPartialContents(
              afterCart,
              addedItem.key,
              requestedQuantity,
              addedQuantity
            );

          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData,
          });
        };

        const quickAddModal =
          this.closest('quick-add-modal');

        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              renderNotification().catch((error) => {
                console.error(error);
                window.location.href = routes.cart_url;
              });
            },
            { once: true }
          );

          quickAddModal.hide(true);
        } else {
          try {
            await renderNotification();
          } catch (error) {
            console.error(error);

            // La adición ya ocurrió. Evitamos repetirla.
            window.location.href = routes.cart_url;
          }
        }

        return true;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') ===
          'true'
        ) {
          return;
        }

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner =
          this.querySelector('.loading__spinner');

        spinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(
            document.activeElement
          );
        }

        config.body = formData;

        const variantId = formData.get('id');

        const quantity =
          parseInt(formData.get('quantity'), 10) || 1;

        const deferred =
          this.createCartLinesUpdateEvent(
            variantId,
            quantity
          );

        // El snapshot es informativo. Si falla,
        // mantenemos el funcionamiento habitual.
        let beforeCart = null;

        try {
          beforeCart = await this.fetchCartSnapshot();
        } catch (error) {
          console.warn(
            'No se pudo consultar el carrito anterior:',
            error
          );
        }

        try {
          const fetchResponse = await fetch(
            routes.cart_add_url,
            config
          );

          const response =
            await fetchResponse.json();

          if (response.status) {
            const wasPartiallyAdded =
              await this.handlePartialAddition(
                response,
                variantId,
                quantity,
                beforeCart,
                deferred
              );

            if (wasPartiallyAdded) {
              return;
            }

            // Error habitual: no se han confirmado
            // unidades añadidas.
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors:
                response.errors ||
                response.description,
              message: response.message,
            });

            this.handleErrorMessage(
              response.description
            );

            this.dispatchCartErrorEvent(
              response.description ||
                response.message,
              'INVALID'
            );

            deferred?.reject(
              new Error(
                response.description ||
                  response.message ||
                  'No se pudo añadir el producto'
              )
            );

            const soldOutMessage =
              this.submitButton.querySelector(
                '.sold-out-message'
              );

            if (soldOutMessage) {
              this.submitButton.setAttribute(
                'aria-disabled',
                true
              );

              this.submitButtonText.classList.add(
                'hidden'
              );

              soldOutMessage.classList.remove(
                'hidden'
              );

              this.error = true;
            }

            return;
          }

          // Adición normal: conserva el funcionamiento
          // y los eventos originales del tema.
          if (!this.cart) {
            this.resolveCartLinesUpdate(deferred);

            window.location.href =
              window.routes.cart_url;

            return;
          }

          this.resolveCartLinesUpdate(deferred);

          const startMarker =
            CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker(
              'add:wait-for-subscribers',
              startMarker
            );
          });

          this.error = false;

          const quickAddModal =
            this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure(
                    'add:paint-updated-sections',
                    () => {
                      this.cart.renderContents(
                        response
                      );
                    }
                  );
                });
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            CartPerformance.measure(
              'add:paint-updated-sections',
              () => {
                this.cart.renderContents(response);
              }
            );
          }
        } catch (error) {
          console.error(error);

          this.dispatchCartErrorEvent(
            error.message || 'Network error',
            'SERVICE_UNAVAILABLE'
          );

          deferred?.reject(error);

          this.handleErrorMessage(
            'No pudimos confirmar la operación. Revisa tu carrito antes de intentarlo nuevamente.'
          );
        } finally {
          this.submitButton.classList.remove(
            'loading'
          );

          if (
            this.cart &&
            this.cart.classList.contains('is-empty')
          ) {
            this.cart.classList.remove(
              'is-empty'
            );
          }

          if (!this.error) {
            this.submitButton.removeAttribute(
              'aria-disabled'
            );
          }

          spinner?.classList.add('hidden');

          CartPerformance.measureFromEvent(
            'add:user-action',
            evt
          );
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(
        variantId,
        quantity
      ) {
        const { CartLinesUpdateEvent } =
          window.StandardEvents || {};

        if (!CartLinesUpdateEvent) {
          return null;
        }

        const deferred =
          CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [
              {
                merchandiseId: variantId,
                quantity,
              },
            ],
            promise: deferred.promise,
          })
        );

        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;

        const { CartLinesUpdateEvent } =
          window.StandardEvents || {};

        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(
                `${routes.cart_url}.json`
              ).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              return deferred.reject(
                new Error(
                  'Missing currency in cart response'
                )
              );
            }

            deferred.resolve({
              cart:
                CartLinesUpdateEvent.createCartFromAjaxResponse(
                  cart
                ),
            });
          })
          .catch((error) => {
            deferred.reject(error);
          });
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } =
          window.StandardEvents || {};

        if (!CartErrorEvent) return;

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code,
          })
        );
      }

      get variantIdInput() {
        return this.form.querySelector(
          '[name=id]'
        );
      }
    }
  );
}
